import {useMemo} from 'react'
import {useTripDetails} from '@/hooks/useTrips'
import {useDriverPosition} from '@/hooks/useDriverPosition'
import useManifestRoute from '@/hooks/useManifestRoute.js'
import buildManifest from '@/utils/manifest'
import {toPoint} from '@/utils/polyline'

/**
 * EVERYTHING THE TRACKING PAGE KNOWS, in one hook: which screen to show, where the car is,
 * and when it gets here.
 *
 * The page below is a layout; this is the thinking. Kept apart because the page has three
 * quite different screens in it and the logic that chooses between them should not be read
 * through JSX.
 */

/**
 * THE FOUR GROUPS, and the boundaries are the product's, not arbitrary.
 *
 * `EnRoute` is the moment the driver sets off for the pickup. It is also the line
 * `isDriverRevealed` in `utils/functions.js` already draws for showing WHO the driver is —
 * the same line, because they are the same question: before the driver has committed to
 * this ride there is nothing to track and nobody to name.
 *
 * `PendingHold` and `PaymentHoldFailed` sit in the waiting group deliberately. They are
 * payment states, not travel states, and a trip in either has no driver on the road.
 */
const WAITING_STATUSES = ['Draft', 'Requested', 'Broadcasted', 'Accepted', 'Assigned', 'PendingHold', 'PaymentHoldFailed']
// `Arrived` is the driver waiting at the pickup — still a car to show, so still tracked.
const APPROACHING_STATUSES = ['EnRoute', 'Arrived']
const RIDING_STATUSES = ['InProgress']
const FINISHED_STATUSES = ['Completed', 'Cancelled', 'NoShow']

export const TRACK_PHASE = {
    loading: 'loading',
    missing: 'missing',
    waiting: 'waiting',
    approaching: 'approaching',
    riding: 'riding',
    finished: 'finished',
}

function phaseFor(trip) {
    const status = trip?.status
    if (!status) return TRACK_PHASE.missing
    if (APPROACHING_STATUSES.includes(status)) return TRACK_PHASE.approaching
    if (RIDING_STATUSES.includes(status)) return TRACK_PHASE.riding
    if (FINISHED_STATUSES.includes(status)) return TRACK_PHASE.finished
    if (WAITING_STATUSES.includes(status)) return TRACK_PHASE.waiting
    // An unknown status is treated as "too early" rather than as an error. A backend that
    // adds a state should not put a passenger on a broken page.
    return TRACK_PHASE.waiting
}

export function useTrackedRide(tripId) {
    /*
     * ⚠️ POLLED AS WELL AS SOCKETED. `.trip.status.updated` arrives on the same channel and
     * `useDriverPosition` already invalidates this query when it does — but a socket that
     * failed to authorise is silent, and a passenger whose ride has started must not be
     * left on the waiting screen because of it. The two-second poll is the floor under the
     * socket, and it already exists.
     */
    const {data, isLoading, isError} = useTripDetails(tripId, {live: true})
    const trip = data?.trip || null

    const phase = isLoading && !trip ? TRACK_PHASE.loading : (isError && !trip ? TRACK_PHASE.missing : phaseFor(trip))
    const tracking = phase === TRACK_PHASE.approaching || phase === TRACK_PHASE.riding

    /* The socket is opened only while there is something to track. Held open through the
       waiting screen it would authorise against a trip the backend does not yet consider
       live, which is a 403 and a scary console line for a page that is working correctly. */
    const driver = useDriverPosition(tripId, {enabled: tracking})

    /* ⚠️ MEMOISED. `buildManifest` returns a fresh array every call, and both the map's
       markers and the route solve below key off this array's IDENTITY. Unmemoised, every
       position frame would rebuild the markers and re-request Directions. */
    /* ⚠️ THE DROP-OFF PIN SITS AT THE ROUTE'S LAST LEG END — the same fix the driver's
       Ongoing page has. The waypoint's own `location` is the place's geocoded centre (e.g.
       the middle of a park), a point no car can reach, so the green line stopped at the
       road and the pin floated in the park. Only the last drop's point moves; its id, type
       and completion still come off the waypoint. No legs → the waypoint point stands. */
    const drops = useMemo(() => {
        const manifest = buildManifest(trip)
        const legs = trip?.route?.legs || []
        const roadEnd = legs.length > 0 ? toPoint(legs[legs.length - 1]?.end_location) : null
        if (!roadEnd || manifest.length === 0) return manifest
        const last = manifest.length - 1
        return manifest.map((d, i) => (i === last ? {...d, point: roadEnd} : d))
    }, [trip])

    /*
     * ONE SOLVE, SHARED. The map draws the line from it and the header reads the arrival
     * time off it — see the note at the top of `TrackDriverMap`.
     *
     * ⚠️ TO THE NEXT OUTSTANDING DROP ONLY. Routed through every remaining stop at once,
     * the line runs past the pickup to the drop-off before the car has arrived, which is a
     * journey nobody is on yet.
     */
    const leg = useMemo(() => {
        const i = drops.findIndex((d) => !d.isCompleted)
        return i === -1 ? [] : drops.slice(i, i + 1)
    }, [drops])

    const route = useManifestRoute(tracking ? driver.position : null, leg)

    return {
        trip,
        phase,
        drops,
        route,
        driver,
        /* Where the car is HEADED, which is what the header names. The first drop still
           outstanding is the pickup while approaching and the destination once aboard, so
           one expression covers both screens. */
        nextStop: leg[0] || null,
        isLoading,
    }
}

export default useTrackedRide
